/* =============================================================================
   PlateX — Dateless Reference · Phase 1 inline fixtures.
   Copy is verbatim from the approved Dateless v13 prototype (build pack §6/§7),
   with two mechanical conversions required by the pack:
     · no em/en dashes as punctuation (ranges use "to")
     · public terminology "Historic Issuing Authorities"
   Do not edit copy here without design sign-off.
   ========================================================================== */

export interface TimelineMilestone {
  yr: string;
  sub?: string;
  plate?: string;   // black-and-silver archive mark shown beside the entry
  heading: string;
  /** Static editorial HTML (bold emphasis only), from the approved prototype. */
  html: string;
}

/** §6.6 Historical timeline — one connected chronology (prototype MIL). */
export const DATELESS_TIMELINE: TimelineMilestone[] = [
  {
    yr: '1903', sub: '23 Nov', plate: 'DY 1', heading: 'The true first',
    html: 'Motoring’s first law, the Motor Car Act of 1903, required every car to carry a registration. The earliest known was issued not in London but in <b>Hastings</b>: DY 1. Somerset, Buckinghamshire, Roxburghshire and Bath all issued marks that same November, while London did not begin until January 1904.',
  },
  {
    yr: '1903', sub: 'December', plate: 'A 1', heading: 'London’s first, and the myth',
    html: 'When London did issue its own first mark, A 1, it went to <b>Earl Russell</b>, an alderman of the London County Council. The tale that he queued all night for it is folklore; every early London single-digit mark went to council politicians or their relatives. A 1 is London’s first plate, not Britain’s.',
  },
  {
    yr: '1903', sub: 'the format', plate: 'AB 12', heading: 'One letter, or two',
    html: 'Each authority was given its own letters, so a registration announced where a car came from. The busiest places received a single letter, London A, Lancashire B, and everywhere else took two. The fewer the letters, the smaller the authority’s stock would turn out to be.',
  },
  {
    yr: '1905', plate: 'A', heading: 'London runs out first',
    html: 'Because the letters were allocated by 1901 population, London held the single letter <b>A</b> and also the most cars in the country. It used up every A-combination within <b>two years</b>, the first authority to run dry. Quieter Staffordshire, issuing E, lasted until 1925.',
  },
  {
    yr: 'c.1932', sub: 'the format', plate: 'ABC 123', heading: 'A third letter',
    html: 'As authorities exhausted their two-letter combinations, a <b>third letter</b> was added to extend them. Three-letter marks became the standard issue through the 1930s, and they still make up the bulk of the dateless registrations that survive today.',
  },
  {
    yr: '1953', plate: '1000 E', heading: 'The reverse',
    html: 'By the 1950s even three letters were running short. Staffordshire’s answer, in 1953, was to <b>reverse the order</b> and place the numbers first, beginning with 1000 E. These reversed marks, issued until 1963, were the last dateless registrations Britain produced.',
  },
  {
    yr: '1963', heading: 'The last dateless marks',
    html: 'In 1963 the system changed completely. A <b>year-suffix</b> letter was added to every new registration so that a car’s age could be read from its plate, and dateless issue stopped for good. Everything registered before this point became a fixed set that can never grow.',
  },
  {
    yr: '1989', sub: 'December', plate: '99 MG', heading: 'The market opens',
    html: 'For twenty-six years those marks only changed hands privately. Then, in 1989, the DVLA began auctioning the registrations it had never issued or had taken back, opening at <b>Christie’s</b> with 74 lots. The first sold, 99 MG, made £8,000; the top lot, 1 A, made £160,000. A finite stock now had a public market.',
  },
  {
    yr: '2025', sub: 'November', plate: '1 F', heading: 'The record, £926,000',
    html: 'As that market matured, prices climbed. In 2014 the number <b>25 O</b> reached £518,480, long the DVLA’s own record. In 2025 the Essex mark <b>1 F</b>, first issued in 1957, sold at <b>Sotheby’s</b> for £926,000, the most any British registration has made in public. None has yet passed £1 million.',
  },
  {
    yr: 'Today', heading: 'A closed set, still the most collected',
    html: 'The set remains closed, and no dateless mark can ever be created again. Yet demand has only grown: since 1989 the DVLA has raised more than <b>£2.9 billion</b> re-selling registrations. The last live auction was held in 2023, and the market is now conducted online, and worldwide.',
  },
];

export const TIMELINE_SOURCES =
  'Sources: the Motor Car Act 1903; DVLA / CVPG registration records; Wikipedia, ' +
  '“Vehicle registration plates of the United Kingdom” and “A1 registration plate.” ' +
  'Auction history and the £2.9bn revenue figure: DVLA / gov.uk (“30 years on”) and reported ' +
  'auction results. Where a “first” is disputed, it is flagged.';

export interface CollectionCard {
  title: string;
  description: string;
  action: string;
  /** routerLink target; null renders a non-navigating card (route not yet built). */
  link: string[] | null;
  /** in-page anchor id used instead of a route (Historic Issuing Authorities). */
  anchor?: string;
}

/** §6.10 Related collections (prototype COLL) with real route targets. */
export const RELATED_COLLECTIONS: CollectionCard[] = [
  {
    title: 'Initials',
    description: 'Registrations whose letters read as a person’s initials, the form in which letter-first dateless marks command their clearest premium.',
    action: 'Explore Initials →', link: ['/plates', 'initials'],
  },
  {
    title: 'Name Plates',
    description: 'Registrations whose characters spell a recognisable name in full.',
    action: 'Explore Names →', link: ['/plates', 'names'],
  },
  {
    title: 'Number Look',
    description: 'Registrations whose visual form reads as a number, a year or a date rather than a code.',
    action: '', link: null,
  },
  {
    title: 'Investment Grade',
    description: 'Registrations whose long-term performance is supported by repeat-sale evidence in the PlateX Index.',
    action: '', link: null,
  },
  {
    title: 'Historic Issuing Authorities',
    description: 'Registrations grouped by the local authority whose letters first issued the mark.',
    action: 'Explore Authorities →', link: null, anchor: 'authorities',
  },
  {
    title: 'Number Ones',
    description: 'Registrations pairing a single figure 1 with a letter or initials, the shortest and most prized dateless form.',
    action: 'Explore Number Ones →', link: ['/plates', 'number-1'],
  },
];

export interface WhereNextCard {
  title: string;
  action: string;
  link: string[] | null;
}

/** Closing dark cards (prototype DEST) with real route targets. */
export const WHERE_NEXT: WhereNextCard[] = [
  { title: 'Number Ones', action: 'The pinnacle →', link: ['/plates', 'number-1'] },
  { title: 'Initials', action: 'Your letters →', link: ['/plates', 'initials'] },
  { title: 'Name Plates', action: 'Your name →', link: ['/plates', 'names'] },
  { title: 'The Index', action: 'The market →', link: null },
];

export interface Chapter {
  id: string;
  label: string;
  hot?: boolean;
}

/** Sticky chapter navigation (§6.1; anchors per the build pack). */
export const CHAPTERS: Chapter[] = [
  { id: 'market', label: 'The market' },
  { id: 'appeal', label: 'Why dateless' },
  { id: 'authorities', label: 'Authorities' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'value', label: 'Value' },
  { id: 'register', label: 'Explore', hot: true },
  { id: 'collections', label: 'Collections' },
];
